import { useMemo } from "react";

import { useGetOnboardingOverviewAPI } from "@/api/onboarding/useOnboardingAPI";
import type { OnboardingModule, OnboardingProgress } from "@/types/onboarding";

export type ProductOverviewState = {
  modules: OnboardingModule[];
  progressByModuleId: Record<string, OnboardingProgress>;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Aggregates the data the Product Overview panel renders: the onboarding
 * modules (the only "ready" API, sorted by their display order) and a lookup
 * of progress by module id for the per-row "X/N" count.
 */
export const useProductOverview = (): ProductOverviewState => {
  const overviewQuery = useGetOnboardingOverviewAPI();
  const modulesResponse = overviewQuery.data?.[0];
  const progressResponse = overviewQuery.data?.[1];

  const modules = useMemo(
    () =>
      [...(modulesResponse?.modules ?? [])].sort((a, b) => a.order - b.order),
    [modulesResponse]
  );

  const progressByModuleId = useMemo(
    () =>
      (progressResponse?.progress ?? []).reduce<
        Record<string, OnboardingProgress>
      >((acc, progress) => {
        acc[progress.module_id] = progress;
        return acc;
      }, {}),
    [progressResponse]
  );

  return {
    modules,
    progressByModuleId,
    isLoading: overviewQuery.isLoading,
    isError: overviewQuery.isError
  };
};
